import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { StatusBadge, CHANNEL, mdLabel } from "@/components/birthdays/shared";
import { Check, X, RotateCcw, ListChecks } from "lucide-react";

export const useBirthdayAction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ url, body }) => api.post(url, body || {}),
    onSuccess: (_, v) => { toast.success(v.ok || "Done"); qc.invalidateQueries({ queryKey: ["birthdays"] }); setTimeout(() => qc.invalidateQueries({ queryKey: ["birthdays"] }), 4000); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"),
  });
};

export const retryGreeting = (act, r) => {
  const unsure = r.status === "uncertain";
  if (!window.confirm(unsure ? `Delivery to ${r.patient_name} is uncertain — they may already have it. Only retry if you've confirmed they didn't get it. Retry now?` : `Send ${r.patient_name}'s birthday greeting again now?`)) return;
  act.mutate({ url: `/birthdays/${r.id}/retry`, body: { confirm_uncertain: unsure }, ok: "Retry queued" });
};

export const GreetingActions = ({ r, act }) => (
  <div className="flex justify-end gap-1">
    {r.status === "pending_approval" && <Button size="sm" className="h-7 bg-[#0B1F44] hover:bg-[#13306a]" disabled={act.isPending} data-testid={`bday-approve-${r.id}`}
      onClick={() => act.mutate({ url: "/birthdays/approve", body: { ids: [r.id] }, ok: `Approved — sending to ${r.patient_name}` })}><Check className="mr-1 h-3.5 w-3.5" />Approve</Button>}
    {["pending_approval", "queued"].includes(r.status) && <Button size="sm" variant="outline" className="h-7" disabled={act.isPending} data-testid={`bday-cancel-${r.id}`}
      onClick={() => window.confirm(`Don't send ${r.patient_name} a birthday greeting this year?`) && act.mutate({ url: `/birthdays/${r.id}/cancel`, ok: "Cancelled" })}><X className="mr-1 h-3.5 w-3.5" />Don't send</Button>}
    {["failed", "uncertain", "cancelled"].includes(r.status) && <Button size="sm" variant="outline" className="h-7" disabled={act.isPending} data-testid={`bday-retry-${r.id}`}
      onClick={() => retryGreeting(act, r)}><RotateCcw className="mr-1 h-3.5 w-3.5" />Retry</Button>}
  </div>
);

export const GreetingRow = ({ r, act, showDate }) => (
  <tr data-testid={`bday-row-${r.id}`} className="border-t border-slate-100 align-top">
    <td className="px-3 py-2.5 font-medium text-slate-800">{r.patient_name}</td>
    <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{mdLabel(r.birthday)}{showDate && <span className="block text-[11px] text-slate-400">greeted {r.greet_date}</span>}</td>
    <td className="px-3 py-2.5 text-slate-600">{r.attempts?.length > 1 ? `${CHANNEL[r.attempts[0].channel]} → ${CHANNEL[r.attempts[r.attempts.length - 1].channel]}` : CHANNEL[r.channel] || "—"}
      {r.email_fallback && !r.attempts?.length && <span className="block text-[11px] text-slate-400">email if text fails</span>}</td>
    <td className="px-3 py-2.5"><StatusBadge s={r.status} testid={`bday-status-${r.id}`} />
      {(r.skip_reason || r.error) && <span className="mt-1 block max-w-xs text-[11px] text-slate-500">{r.skip_reason || r.error}</span>}</td>
    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-400">{r.sent_at ? fmtDateTime(r.sent_at) : r.updated_at ? fmtDateTime(r.updated_at) : ""}
      {r.attempts?.length > 1 && <span className="block">{r.attempts.length} attempts</span>}</td>
    <td className="px-3 py-2.5"><GreetingActions r={r} act={act} /></td>
  </tr>
);

export const GreetingTable = ({ rows, act, showDate, empty }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr><th className="px-3 py-2">Patient</th><th className="px-3 py-2">Birthday</th><th className="px-3 py-2">Channel</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Time</th><th /></tr>
      </thead>
      <tbody>{rows.map((r) => <GreetingRow key={r.id} r={r} act={act} showDate={showDate} />)}</tbody>
    </table>
    {rows.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500">{empty}</p>}
  </div>
);

export const BirthdayToday = ({ data }) => {
  const act = useBirthdayAction();
  const pending = data.today.filter((r) => r.status === "pending_approval").length;
  return (
    <div className="space-y-3" data-testid="bday-today">
      <div className="flex flex-wrap items-center gap-2">
        {!data.list_prepared && <Button variant="outline" disabled={act.isPending} data-testid="bday-prepare-today"
          onClick={() => act.mutate({ url: "/birthdays/prepare-today", ok: "Today's list is ready" })}><ListChecks className="mr-2 h-4 w-4" />Prepare today's list now</Button>}
        {pending > 0 && <Button className="bg-[#0B1F44] hover:bg-[#13306a]" disabled={act.isPending} data-testid="bday-approve-all"
          onClick={() => window.confirm(`Send ${pending} birthday greeting${pending === 1 ? "" : "s"} now?`) && act.mutate({ url: "/birthdays/approve", body: { all_today: true }, ok: `Approved ${pending} — sending now` })}>
          <Check className="mr-2 h-4 w-4" />Approve all {pending}</Button>}
        <span className="text-xs text-slate-500">{data.list_prepared ? "Today's list was prepared." : `The list is prepared automatically at ${data.settings.send_time} when greetings are switched on.`}</span>
      </div>
      <GreetingTable rows={data.today} act={act} empty="No birthday greetings for today yet." />
    </div>
  );
};
